// Phase B's combat stream in the spike (gates B1 and B2): the dojo scene (`?scene=dojo`, ./dojo.ts) on @rpg/combat and
// @rpg/abilities, with the bodies module and the impact director since the merge, its view (./view.ts), the content (./content.ts), the planted defects and the handle keys under
// `__rpg.combat`. The clock's split hit-stop and slow-mo are @rpg/runtime's (main.ts turns the freeze mode on from the
// tuning table); its two planted defects join this group, beside the clock they break.
import type * as THREE from "three";
import { COMBAT_FAULTS, type FighterView, type Intent, type IntentKind } from "@rpg/combat";
import type { Invariant } from "@rpg/harness";
import type { Figure, PoseSample } from "@rpg/figure";
import type { CameraRig } from "@rpg/camera";
import type { EventBus } from "@rpg/kernel";
import type { GameEvents } from "../core";
import type { LowPolyLook } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import type { ValleyVariant } from "../valley/scene";
import { combatContent, type CombatContent } from "./content";
import { DOJO_SPAWN, dojoVariant, type Dojo } from "./dojo";
import { mountCombatView, type CombatView } from "./view";

/** The combat stream's planted defects by their spike names. */
export const COMBAT_FAULT_NAMES = ["combat-parry-shift", "combat-active-extra-step", "hitstop-freezes-view", "hitstops-sum"] as const;

/** The fault group for the spike's one faults record (every scene: the clock's defects apply to all). Reads `?faults=`
 *  for its own names on creation. */
export function combatFaultGroup(runtime: Runtime, search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  const get = (n: string): boolean =>
    n === "combat-parry-shift"
      ? COMBAT_FAULTS.parryWindowShift
      : n === "combat-active-extra-step"
        ? COMBAT_FAULTS.activeExtraStep
        : n === "hitstop-freezes-view"
          ? runtime.clock.faults.hitStopFreezesView
          : n === "hitstops-sum"
            ? runtime.clock.faults.hitStopsSum
            : false;
  const set = (n: string, on: boolean): void => {
    if (n === "combat-parry-shift") COMBAT_FAULTS.parryWindowShift = on;
    if (n === "combat-active-extra-step") COMBAT_FAULTS.activeExtraStep = on;
    if (n === "hitstop-freezes-view") runtime.clock.faults.hitStopFreezesView = on;
    if (n === "hitstops-sum") runtime.clock.faults.hitStopsSum = on;
  };
  const named = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of COMBAT_FAULT_NAMES) set(n, named.includes(n));
  return { names: COMBAT_FAULT_NAMES, get, set };
}

/** Touch buttons the dojo uses (hidden in the layout until combat needs them; the input notes leave this to combat). */
export const COMBAT_TOUCH_BUTTONS = ["skill", "bigMove", "block", "weaponSwitch"] as const;

/** The dojo's parts for the sim mount. */
export interface DojoMount {
  variant: ValleyVariant;
  /** The dojo once the scene is set up. */
  dojo(): Dojo;
  content: CombatContent;
  /** Mount the view on the valley's dressed scene (the look and the hero's figure), inside the look's warm-up (the
   *  returned function clears what the impact director showed for it). */
  mountView(o: { scene: THREE.Scene; look: LowPolyLook; hero: { figure: Figure }; events: EventBus<GameEvents>; rig: CameraRig | null; setting?: (id: string) => unknown }): () => void;
  /** The hero's action poses and reactions at a frame's alpha (the bodies module's track). */
  heroPoses(alpha: number): PoseSample | null;
  /** Whether the hero has its weapon out (always, in the dojo). */
  acting(): boolean;
  /** Keys for `window.__rpg.combat`. */
  handle(): ReturnType<typeof makeHandle>;
  invariants(): Invariant[];
}

/** Build the dojo's parts from the content. */
export function dojoMount(o: { content: SpikeContent; runtime: Runtime }): DojoMount {
  const content = combatContent(o.content.registry);
  let dojo: Dojo | null = null;
  let view: CombatView | null = null;
  const variant = dojoVariant({ content, spike: o.content, runtime: o.runtime, ready: (d) => void (dojo = d) });
  const got = (): Dojo => {
    if (!dojo) throw new Error("dojo: not set up yet");
    return dojo;
  };
  return {
    variant,
    dojo: got,
    content,
    mountView(v) {
      const d = got();
      view = mountCombatView({ runtime: o.runtime, scene: v.scene, look: v.look, content: o.content, combat: content, dojo: d, hero: v.hero, events: v.events, rig: v.rig, ...(v.setting ? { setting: v.setting } : {}) });
      const feet = d.pose(d.state().hero).feet;
      return view.feedback.warm([feet[0], feet[1], feet[2] - 2]);
    },
    heroPoses(alpha) {
      const d = got();
      const hero = d.state().hero;
      const card = content.fighters.get("hero")?.card.id;
      if (!card) return null;
      const t = d.bodies.state().t + (alpha - 1) * o.runtime.clock.dt;
      return d.bodies.sample(hero, card, t);
    },
    acting: () => true,
    handle: () => makeHandle(got(), o.runtime, () => view),
    invariants: () => {
      const d = got();
      return [
        () => {
          for (const e of d.combat.fighters()) {
            const v = d.combat.state(e);
            const nums = [v.hp, v.hpMax, v.poise, v.stamina, v.energy];
            if (!nums.every(Number.isFinite)) return `non-finite combat state (fighter ${e})`;
            if (v.hp < 0 || v.hp > v.hpMax + 1e-9) return `fighter ${e} hp ${v.hp} outside 0..${v.hpMax}`;
            if (v.stamina < -1e-9 || v.stamina > v.staminaMax + 1e-9) return `fighter ${e} stamina ${v.stamina} outside 0..${v.staminaMax}`;
            if (!v.alive && v.hp !== 0) return `fighter ${e} defeated with hp ${v.hp}`;
          }
          return null;
        },
      ];
    },
  };
}

/** Who a handle call means: "hero", a dummy's place ("static", "attacker", "slammer"), or an entity id. */
type Who = "hero" | string | number;

function makeHandle(d: Dojo, runtime: Runtime, view: () => CombatView | null) {
  const entity = (who: Who): number => (typeof who === "number" ? who : who === "hero" ? d.state().hero : d.dummy(who));
  const views = (): Record<string, FighterView & { entity: number }> => {
    const out: Record<string, FighterView & { entity: number }> = { hero: { entity: d.state().hero, ...d.combat.state(d.state().hero) } };
    for (const m of d.state().dummies) out[m.place] = { entity: m.entity, ...d.combat.state(m.entity) };
    return out;
  };
  return {
    /** Every fighter's view, the dojo's step, the defence windows in steps, the clock's hit-stop and slow-mo. */
    state: () => ({
      step: d.state().step,
      fighters: views(),
      windows: { ...d.combat.windows },
      scripted: d.state().scripted,
      clock: { frozen: runtime.clock.frozen, hitStopLeft: runtime.clock.hitStopRemaining, slowMo: runtime.clock.slowMoScale, slowMoLeft: runtime.clock.slowMoRemaining, effective: runtime.clock.effectiveScale, view: runtime.clock.viewScale },
      telegraphs: view()?.feedback.telegraphs.state() ?? [],
      held: view()?.held() ?? null,
    }),
    /** Ask for an intent on the dojo step `inSteps` from now (1: the next step), made inside that step after combat's
     *  timers advance, as the input's are. `results()` says whether it was taken. */
    queue: (who: Who, intent: { kind: IntentKind; dir?: [number, number, number] }, inSteps = 1): void => {
      d.state().queue.push({ who: entity(who), intent: intent as Intent, at: d.state().step + Math.max(1, Math.floor(inSteps)) });
    },
    results: () => [...d.state().results],
    /** Combat events logged since dojo step `since` (default all kept), each with the step it happened on. */
    events: (since = -1) => d.log.filter((e) => e.step >= since).map((e) => ({ step: e.step, name: e.name, ...e.payload })),
    /** Live hit-volume steps since dojo step `since`. */
    sweeps: (since = -1) => d.sweeps.filter((s) => s.step >= since),
    /** The dummies swing on their timers (true) or stand (false). */
    script: (on: boolean): void => void (d.state().scripted = on),
    /** Everyone back to full and idle, nothing queued, the hero at the spawn (or at x, z facing yaw). */
    reset: (at?: { x: number; z: number; yaw: number }): void => {
      for (const e of d.combat.fighters()) {
        d.combat.revive(e);
        const f = d.fighter(e)!;
        f.stats.effects = [];
        d.combat.abilities.setBase(f.stats, "energy", 0);
        f.sinceHit = 999;
        f.sinceSpend = 999;
      }
      const s = d.state();
      s.queue = [];
      s.results = [];
      s.respawn = {};
      s.heldBlock = false;
      const p = at ?? DOJO_SPAWN;
      d.rig.warpHero(p.x, 0, p.z, p.yaw);
    },
    /** Change a fighter's weapon (between moves). */
    setWeapon: (who: Who, weapon: string | null): boolean => d.combat.setWeapon(entity(who), weapon),
    /** Set a stat's base (a test filling the big move's charge). */
    setStat: (who: Who, stat: string, value: number): void => d.combat.abilities.setBase(d.fighter(entity(who))!.stats, stat, value),
    /** The moves' frame data as combat runs them (steps at the sim's rate), and the weapons' sets. */
    moves: () => ({
      moves: [...d.combat.moves.values()].map((m) => ({ id: m.id, kind: m.kind, windup: m.windup, active: m.active, recovery: m.recovery, unblockable: m.unblockable === true, telegraph: m.telegraph?.style ?? null })),
      weapons: [...d.combat.weapons.values()].map((w) => ({ ...w })),
    }),
    /** The late phase's per-frame numbers (steps, view dt, real dt, frozen) since the last reset. */
    frames: () => view()?.frames() ?? [],
    resetFrames: (): void => view()?.resetFrames(),
    entity,
  };
}
